import {FetcherFactory} from '@s-ui/domain'

import UserServicesFactory from '../../user/Services/factory.js'
import FetcherRepositoriesFactory from '../Repositories/factory.js'
import FetcherServicesFactory from '../Services/factory.js'
import AuthenticationFetcher from './AuthenticationFetcher/index.js'
import DraftCreateResponseTemplate from './FakeFetcher/DraftCreateResponseTemplate.js'
import DraftPublishResponseTemplate from './FakeFetcher/DraftPublishResponseTemplate.js'
import DraftResponseTemplate from './FakeFetcher/DraftResponseTemplate.js'
import DraftUpdateResponseTemplate from './FakeFetcher/DraftUpdateResponseTemplate.js'
import FakeFetcher from './FakeFetcher/index.js'
import VehicleInfoResponseTemplate from './FakeFetcher/VehicleInfoResponseTemplate.js'
import {FetchOrFail} from './FetchOrFail/index.js'
import {ProxyFetcher} from './ProxyFetcher/index.js'

/**
 * @class
 * @implements FetcherFactory
 * Factory methods for fetcher service
 */
export default class MotorFetcherFactory {
  static fetcherConfig = ({config}) => {
    const CHANNEL_DESKTOP = 'web-desktop'
    const CHANNEL_MOBILE = 'web-mobile'
    const isMobile = config.get('isMobile')
    const deviceType = isMobile ? CHANNEL_MOBILE : CHANNEL_DESKTOP
    const platform = config.get('TENANT')
    const url = config.get('X-Adevinta-Page-Url')
    const referer = config.get('X-Adevinta-Referer')
    const {ADOBE_MC_VISITOR_ID, CHANNEL, EUCONSENT_V2, SESSION_ID, TENANT, USER_ID, URL, REFERER, IMPERVA} =
      config.get('REQUEST_HEADERS')
    const defaultTimeout = config.get('DEFAULT_REQUEST_TIMEOUT')
    const {
      ADOBE_MC_VISITOR_COOKIE_KEY,
      COOKIE_CMP_KEY,
      IMPERVA_TOKEN_COOKIE_KEY,
      SEGMENT_ANONYMOUS_COOKIE_KEY,
      SEGMENT_USER_ID_COOKIE_KEY
    } = config.get('COMMON').COOKIES
    const {
      [ADOBE_MC_VISITOR_COOKIE_KEY]: adobeMCVisitor,
      [COOKIE_CMP_KEY]: euConsentV2,
      [SEGMENT_ANONYMOUS_COOKIE_KEY]: segmentAnonymousUserId = '',
      [SEGMENT_USER_ID_COOKIE_KEY]: segmentUserId,
      [IMPERVA_TOKEN_COOKIE_KEY]: impervaToken
    } = config.get('cookie') || {}

    const adobeMCVisitorId = adobeMCVisitor?.split('|')[4]
    const baseURL = config.get('API_URL')

    // Debug log
    if (!baseURL) {
      console.error('[FetcherFactory] API_URL is undefined!', {
        stage: process.env.STAGE,
        nodeEnv: process.env.NODE_ENV,
        configKeys: Object.keys(config._config || {}).slice(0, 20)
      })
    }

    return {
      ...(baseURL && {baseURL}),
      ...(typeof defaultTimeout === 'number' && {timeout: defaultTimeout}),
      headers: {
        [TENANT]: platform,
        [CHANNEL]: deviceType,
        [SESSION_ID]: segmentAnonymousUserId,
        ...(adobeMCVisitorId && {[ADOBE_MC_VISITOR_ID]: adobeMCVisitorId}),
        ...(euConsentV2 && {[EUCONSENT_V2]: euConsentV2}),
        ...(impervaToken && {[IMPERVA]: impervaToken}),
        ...(referer && {[REFERER]: referer}),
        ...(segmentUserId && {[USER_ID]: segmentUserId}),
        ...(url && {[URL]: url})
      }
    }
  }

  /**
   * @param {Object} deps
   * @param {Config} deps.config
   */
  static httpAuthenticationFetcher = ({config}) =>
    new AuthenticationFetcher({
      httpFetcher: MotorFetcherFactory.httpFetcher({config}),
      repository: FetcherRepositoriesFactory.cookieFetcherRepository({config}),
      getCurrentUserService: UserServicesFactory.getCurrentUserService({
        config
      }),
      getAuthenticationTokenService: FetcherServicesFactory.getAuthenticationTokenService({config}),
      config
    })

  /**
   * @param {Object} deps
   * @param {Config} deps.config
   */
  static httpFetcher = ({config}) =>
    FetcherFactory.httpFetcher({
      config: MotorFetcherFactory.fetcherConfig({config})
    })

  /**
   * @param {Object} deps
   * @param {Config} deps.config
   */
  static fakeFetcher = ({config}) =>
    new FakeFetcher({
      config,
      vehicleInfoResponseTemplate: MotorFetcherFactory.vehicleInfoResponseTemplate(),
      draftResponseTemplate: MotorFetcherFactory.draftResponseTemplate(),
      draftCreateResponseTemplate: MotorFetcherFactory.draftCreateResponseTemplate(),
      draftUpdateResponseTemplate: MotorFetcherFactory.draftUpdateResponseTemplate(),
      draftPublishResponseTemplate: MotorFetcherFactory.draftPublishResponseTemplate()
    })

  static vehicleInfoResponseTemplate = () => new VehicleInfoResponseTemplate()

  static draftResponseTemplate = () => new DraftResponseTemplate()

  static draftCreateResponseTemplate = () => new DraftCreateResponseTemplate()

  static draftUpdateResponseTemplate = () => new DraftUpdateResponseTemplate()

  static draftPublishResponseTemplate = () => new DraftPublishResponseTemplate()

  static proxyFetcher = ({config}) =>
    new ProxyFetcher({
      httpFetcher: MotorFetcherFactory.httpFetcher({config}),
      authenticationFetcher: MotorFetcherFactory.httpAuthenticationFetcher({
        config
      }),
      fakeFetcher: MotorFetcherFactory.fakeFetcher({config})
    })

  static fetchOrFail = ({config}) => {
    return FetchOrFail.create({
      config
    })
  }
}
